import { ConnectError } from '@bufbuild/connect';
import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@/shared/api/api';
import { ReportService } from '@/shared/api/protocol-ts/api/core/svc_report_connectweb.ts';
import {
  AddToothDisplaySliceReq,
  AddToothDisplaySliceResp,
  ApproveReportToothReq,
  ApproveReportToothResp,
  CreateReportToothConditionsReq,
  CreateReportToothConditionsResp,
  DeleteReportReq,
  DeleteReportResp,
  DisapproveReportToothReq,
  DisapproveReportToothResp,
  GenerateSuggestedToothTreatmentReq,
  GenerateSuggestedToothTreatmentResp,
  GetReportsByStudyAssetAttributesReq,
  GetReportsByStudyAssetAttributesResp,
  ListReportPanoramasWithConditionsReq,
  ListReportPanoramasWithConditionsResp,
  MarkToothAsDeletedReq,
  MarkToothAsDeletedResp,
  MarkToothDetectionAsDeletedReq,
  MarkToothDetectionAsDeletedResp,
  PrintReportReq,
  PrintReportResp,
  RemoveAllToothDisplaySlicesReq,
  RemoveAllToothDisplaySlicesResp,
  RemoveToothDisplaySliceReq,
  RemoveToothDisplaySliceResp,
  RequestReportReq,
  RequestReportResp,
  ResetReportToothReq,
  ResetReportToothResp,
  RestoreToothDisplaySlicesReq,
  RestoreToothDisplaySlicesResp,
  SetImageMetaOrientationAngleReq,
  SetImageMetaOrientationAngleResp,
  SetImageMetaViewOptionsReq,
  SetImageMetaViewOptionsResp,
  SetReportConclusionReq,
  SetReportConclusionResp,
  SetReportExtendedConclusionReq,
  SetReportExtendedConclusionResp,
  SetReportMedicalImageViewOptionsReq,
  SetReportMedicalImageViewOptionsResp,
  SetReportPrintSettingsReq,
  SetReportPrintSettingsResp,
  SetReportSettingsReq,
  SetReportSettingsResp,
  SetReportToothCommentReq,
  SetReportToothCommentResp,
  SetReportToothConditionsUserDecisionReq,
  SetReportToothConditionsUserDecisionResp,
  SetReportToothConditionUserScoreAndApproveToothReq,
  SetReportToothConditionUserScoreAndApproveToothResp,
  SetROIReq,
  SetROIResp,
  SetToothDetectionNumerationAndReviveReq,
  SetToothDetectionNumerationAndReviveResp,
  SetToothFullHeadMPRSliceAnnotationsReq,
  SetToothFullHeadMPRSliceAnnotationsResp,
  SetToothFullHeadMPRSliceEditorStateReq,
  SetToothFullHeadMPRSliceEditorStateResp,
  SetToothFullHeadMPRSliceSelectionReq,
  SetToothFullHeadMPRSliceSelectionResp,
  SetToothFullHeadMPRSliceViewOptionsReq,
  SetToothFullHeadMPRSliceViewOptionsResp,
  SetToothMPRSliceOptionsResp,
  SetToothNumerationAndReviveReq,
  SetToothNumerationAndReviveResp,
  SignReportReq,
  SignReportResp,
  UnsignReportReq,
  UnsignReportResp,
  UpdateTeethStateReq,
  UpdateTeethStateResp,
} from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import { type ToothFullHeadMPR } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { SliceName } from '@/shared/config';
import { conditionIsUncertain } from '@/shared/embeddedLibs/conditionsAndMasks';
import { createThunkGenerator } from '@/shared/lib';

// TODO: [4/h] Discuss the possibility of creating an additional 'services' layer in the FSD between entities and features
import { ModalID, modalModel } from '@/entities/modal';

import type { RootState } from '@/app/model/store';

import {
  CheckReportSignatureModalText,
  type ToothMPRSliceOptionsRequest,
} from '../config';
import {
  queueFullHeadMprWrite,
  resolveToothMPRSliceOptionsWrite,
} from '../lib';

export type ReportRequests = keyof typeof ReportService.methods;

const generateReportThunk = createThunkGenerator<ReportRequests>(
  SliceName.reports,
);

const executeFullHeadMprWrite = <
  Response extends { ToothFullHeadMPR?: ToothFullHeadMPR },
>(
  request: { ToothID?: string },
  write: () => Promise<Response>,
) => queueFullHeadMprWrite({ toothID: request.ToothID ?? '', write });

export const getReportsByStudyAssetAttributes = generateReportThunk<
  GetReportsByStudyAssetAttributesReq,
  GetReportsByStudyAssetAttributesResp
>(
  'getReportsByStudyAssetAttributes',
  api.report.getReportsByStudyAssetAttributes,
);

export const listReportPanoramasWithConditions = generateReportThunk<
  ListReportPanoramasWithConditionsReq,
  ListReportPanoramasWithConditionsResp
>(
  'listReportPanoramasWithConditions',
  api.report.listReportPanoramasWithConditions,
);

export const setReportToothConditionUserScoreAndApproveTooth =
  generateReportThunk<
    SetReportToothConditionUserScoreAndApproveToothReq,
    SetReportToothConditionUserScoreAndApproveToothResp
  >(
    'setReportToothConditionUserScoreAndApproveTooth',
    api.report.setReportToothConditionUserScoreAndApproveTooth,
  );

export const setReportToothConditionsUserDecision = generateReportThunk<
  SetReportToothConditionsUserDecisionReq,
  SetReportToothConditionsUserDecisionResp
>(
  'setReportToothConditionsUserDecision',
  api.report.setReportToothConditionsUserDecision,
);

export const createReportToothConditions = generateReportThunk<
  CreateReportToothConditionsReq,
  CreateReportToothConditionsResp
>('createReportToothConditions', api.report.createReportToothConditions);

export const setReportSettings = generateReportThunk<
  SetReportSettingsReq,
  SetReportSettingsResp
>('setReportSettings', api.report.setReportSettings);

export const requestReport = generateReportThunk<
  RequestReportReq,
  RequestReportResp
>('requestReport', api.report.requestReport);

export const deleteReport_DEPRECATED = generateReportThunk<
  DeleteReportReq,
  DeleteReportResp
>('deleteReport', api.report.deleteReport);

export const setROI = generateReportThunk<SetROIReq, SetROIResp>(
  'setROI',
  api.report.setROI,
);

export const setReportConclusion = generateReportThunk<
  SetReportConclusionReq,
  SetReportConclusionResp
>('setReportConclusion', api.report.setReportConclusion);

export const setReportExtendedConclusion = generateReportThunk<
  SetReportExtendedConclusionReq,
  SetReportExtendedConclusionResp
>('setReportExtendedConclusion', api.report.setReportExtendedConclusion);

export const setReportPrintSettings = generateReportThunk<
  SetReportPrintSettingsReq,
  SetReportPrintSettingsResp
>('setReportPrintSettings', api.report.setReportPrintSettings);

export const printReport = generateReportThunk<PrintReportReq, PrintReportResp>(
  'printReport',
  api.report.printReport,
);

export const signReport = generateReportThunk<SignReportReq, SignReportResp>(
  'signReport',
  api.report.signReport,
);

export const unsignReport = generateReportThunk<
  UnsignReportReq,
  UnsignReportResp
>('unsignReport', api.report.unsignReport);

export const setReportToothComment = generateReportThunk<
  SetReportToothCommentReq,
  SetReportToothCommentResp
>('setReportToothComment', api.report.setReportToothComment);

// WARNING: DO NOT USE THIS METHOD IN RELEASE 1.0.0
export const setReportToothNumeration = generateReportThunk<
  SetToothNumerationAndReviveReq,
  SetToothNumerationAndReviveResp
>('setToothNumerationAndRevive', api.report.setToothNumerationAndRevive);

export const resetReportTooth = generateReportThunk<
  ResetReportToothReq,
  ResetReportToothResp
>('resetReportTooth', api.report.resetReportTooth);

export const updateTeethState = generateReportThunk<
  UpdateTeethStateReq,
  UpdateTeethStateResp
>('updateTeethState', api.report.updateTeethState);

/**
 * @deprecated use updateTeethState instead
 */
export const setToothNumerationAndRevive = generateReportThunk<
  SetToothNumerationAndReviveReq,
  SetToothNumerationAndReviveResp
>('setToothNumerationAndRevive', api.report.setToothNumerationAndRevive);

/**
 * @deprecated use updateTeethState instead
 */
export const setToothDetectionNumerationAndRevive = generateReportThunk<
  SetToothDetectionNumerationAndReviveReq,
  SetToothDetectionNumerationAndReviveResp
>(
  'setToothDetectionNumerationAndRevive',
  api.report.setToothDetectionNumerationAndRevive,
);

// TODO: (4|h) Make feature to avoid enitites collision (break FSD rules) here
export const approveAllTeethAndSignReport = createAsyncThunk(
  `${SliceName.reports}/approveAllTeethAndSignReport`,
  async (reportID: string, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const teethConditionIDs = state.condition.ids.filter((id) =>
      conditionIsUncertain(state.condition.entities[id]?.Certainty),
    ) as string[];

    const request = {
      ReportID: reportID,
      TeethConditionIDs: teethConditionIDs,
    };

    try {
      const response = await api.report.approveAllTeethAndSignReport(request);

      return response;
    } catch (error) {
      const connectError = ConnectError.from(error);

      return rejectWithValue(connectError);
    }
  },
);

export const setReportToothApproved = generateReportThunk<
  ApproveReportToothReq,
  ApproveReportToothResp
>('approveReportTooth', api.report.approveReportTooth);

export const setReportToothDisapproved = generateReportThunk<
  DisapproveReportToothReq,
  DisapproveReportToothResp
>('disapproveReportTooth', api.report.disapproveReportTooth);

export const addToothDisplaySlice = generateReportThunk<
  AddToothDisplaySliceReq,
  AddToothDisplaySliceResp
>('addToothDisplaySlice', api.report.addToothDisplaySlice);

export const removeToothDisplaySlice = generateReportThunk<
  RemoveToothDisplaySliceReq,
  RemoveToothDisplaySliceResp
>('removeToothDisplaySlice', api.report.removeToothDisplaySlice);

export const removeAllToothDisplaySlices = generateReportThunk<
  RemoveAllToothDisplaySlicesReq,
  RemoveAllToothDisplaySlicesResp
>('removeAllToothDisplaySlices', api.report.removeAllToothDisplaySlices);

export const restoreToothDisplaySlices = generateReportThunk<
  RestoreToothDisplaySlicesReq,
  RestoreToothDisplaySlicesResp
>('restoreToothDisplaySlices', api.report.restoreToothDisplaySlices);

export const setToothMPRSliceOptions = generateReportThunk<
  ToothMPRSliceOptionsRequest,
  SetToothMPRSliceOptionsResp
>(
  'setToothMPRSliceOptions',
  api.report.setToothMPRSliceOptions,
  (request, execute, { getState }) =>
    queueFullHeadMprWrite({
      toothID: request.ToothID ?? '',
      write: (previous) =>
        execute(
          resolveToothMPRSliceOptionsWrite({
            previous,
            request,
            stored: (getState() as RootState).reports.fullHeadMprByToothID[
              request.ToothID ?? ''
            ],
          }),
        ),
    }),
);

export const setToothFullHeadMPRSliceSelection = generateReportThunk<
  SetToothFullHeadMPRSliceSelectionReq,
  SetToothFullHeadMPRSliceSelectionResp
>(
  'setToothFullHeadMPRSliceSelection',
  api.report.setToothFullHeadMPRSliceSelection,
  executeFullHeadMprWrite,
);

export const setToothFullHeadMPRSliceAnnotations = generateReportThunk<
  SetToothFullHeadMPRSliceAnnotationsReq,
  SetToothFullHeadMPRSliceAnnotationsResp
>(
  'setToothFullHeadMPRSliceAnnotations',
  api.report.setToothFullHeadMPRSliceAnnotations,
  executeFullHeadMprWrite,
);

export const setToothFullHeadMPRSliceViewOptions = generateReportThunk<
  SetToothFullHeadMPRSliceViewOptionsReq,
  SetToothFullHeadMPRSliceViewOptionsResp
>(
  'setToothFullHeadMPRSliceViewOptions',
  api.report.setToothFullHeadMPRSliceViewOptions,
  executeFullHeadMprWrite,
);

export const setToothFullHeadMPRSliceEditorState = generateReportThunk<
  SetToothFullHeadMPRSliceEditorStateReq,
  SetToothFullHeadMPRSliceEditorStateResp
>(
  'setToothFullHeadMPRSliceEditorState',
  api.report.setToothFullHeadMPRSliceEditorState,
  executeFullHeadMprWrite,
);

export const setImageMetaViewOptions = generateReportThunk<
  SetImageMetaViewOptionsReq,
  SetImageMetaViewOptionsResp
>('setImageMetaViewOptions', api.report.setImageMetaViewOptions);

export const setReportMedicalImageViewOptions = generateReportThunk<
  SetReportMedicalImageViewOptionsReq,
  SetReportMedicalImageViewOptionsResp
>(
  'setReportMedicalImageViewOptions',
  api.report.setReportMedicalImageViewOptions,
);

export const generateSuggestedToothTreatment = generateReportThunk<
  GenerateSuggestedToothTreatmentReq,
  GenerateSuggestedToothTreatmentResp
>(
  'generateSuggestedToothTreatment',
  api.report.generateSuggestedToothTreatment,
);

export const checkReportSignatureThunk = createAsyncThunk(
  `${SliceName.reports}/checkReportSignature`,
  async (
    options: {
      modalText: CheckReportSignatureModalText;
      toothID?: string;
      onSignatureChecked: () => Promise<void>;
      onCancel?: () => void;
    },
    { dispatch, getState },
  ) => {
    const { modalText, onSignatureChecked, onCancel } = options;

    const state = getState() as RootState;
    const isReportSigned =
      state.reports.currentReport?.Signature?.UserSignatures.length;

    if (!isReportSigned) {
      await onSignatureChecked();
    } else {
      const openConfirmModal = async (onConfirm: () => Promise<void>) => {
        dispatch(
          modalModel.actions.openModal({
            modalID: ModalID.Confirm,
            data: {
              title: modalText.title,
              okText: modalText.confirm,
              description: modalText.description,
              bodyClassName: 'p2',
              cancelButtonVariant: 'tertiary',
              onConfirm: async () => {
                dispatch(modalModel.actions.closeModal(ModalID.Confirm));

                await onConfirm();
              },
              onCancel,
            },
          }),
        );
      };

      await openConfirmModal(onSignatureChecked);
    }
  },
);

export const deleteReport = createAsyncThunk(
  `${SliceName.reports}/DeleteReport`,
  async (reportID: string, { rejectWithValue }) => {
    try {
      const response = await api.report.deleteReport({
        ReportID: reportID as string,
      });

      return response;
    } catch (error) {
      const connectError = ConnectError.from(error);

      return rejectWithValue(connectError);
    }
  },
);

export const archiveReport = createAsyncThunk(
  `${SliceName.reports}/ArchiveReport`,
  async (reportID: string, { rejectWithValue }) => {
    try {
      const response = await api.report.archiveReport({
        ReportID: reportID,
      });

      return response;
    } catch (error) {
      const connectError = ConnectError.from(error);

      return rejectWithValue(connectError);
    }
  },
);

export const restoreReport = createAsyncThunk(
  `${SliceName.reports}/RestoreReport`,
  async (reportID: string, { rejectWithValue }) => {
    try {
      const response = await api.report.restoreReport({
        ReportID: reportID,
      });

      return response;
    } catch (error) {
      const connectError = ConnectError.from(error);

      return rejectWithValue(connectError);
    }
  },
);

export const setImageMetaOrientationAngle = generateReportThunk<
  SetImageMetaOrientationAngleReq,
  SetImageMetaOrientationAngleResp
>('setImageMetaOrientationAngle', api.report.setImageMetaOrientationAngle);

/**
 * @deprecated use updateTeethState instead
 */
export const markToothAsDeleted = generateReportThunk<
  MarkToothAsDeletedReq,
  MarkToothAsDeletedResp
>('markToothAsDeleted', api.report.markToothAsDeleted);

/**
 * @deprecated use updateTeethState instead
 */
export const markToothDetectionAsDeleted = generateReportThunk<
  MarkToothDetectionAsDeletedReq,
  MarkToothDetectionAsDeletedResp
>('markToothDetectionAsDeleted', api.report.markToothDetectionAsDeleted);
